import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import {
  FolderOpen, ChevronDown, ChevronRight, Search, Upload, FileText, Trash2,
  Download, Loader2, Pencil, X, Check, Tag, FileSignature, FileCheck2, BookOpen,
  ShieldCheck, Landmark, Megaphone, Map, FileArchive, Link2,
} from "lucide-react";
import api from "../../lib/api";
import { kickOffAutoOcrBatch } from "../../lib/autoOcr";
import { toast } from "sonner";
import { usePortalPerms } from "../../context/PortalPermsContext";
import ReportsTechnicalSection from "./ReportsTechnicalSection";

/*
 * Customer Profile Documents Hub — 9 categorised folders (Phase 3, Iter 349.q).
 *
 * Powered by:
 *   GET    /api/customers/{cid}/profile-documents?category=&q=
 *   POST   /api/customers/{cid}/profile-documents (multipart, ?category=, ?description=)
 *   PATCH  /api/customers/{cid}/profile-documents/{docId}
 *   DELETE /api/customers/{cid}/profile-documents/{docId}
 *
 * Portal mirror: GET /api/portal/profile-documents (Client role).
 *
 * This block is shared between the staff customer profile and the client
 * portal — `apiBase` toggles between `/customers/{cid}` and `/portal`.
 *
 * [Iter 349.q — Dirk, 2026-02-27] Per-folder RBAC. Each category now has
 * its OWN portal perm (`portal.docs.<folder>.read`) so an owner can grant
 * per-folder access without opening broader gates. Empty folders are
 * hidden client-side (Dirk 2026-09-14: "don't show empty folders").
 * Preset matrix (owner-set defaults):
 *   Contracts & SLAs     → owner only
 *   Proposals & Designs  → owner only
 *   Manuals & Datasheets → owner + maintenance
 *   Warranties           → owner + maintenance
 *   Account Docs         → owner + accounts
 *   Notices & Bulletins  → owner + accounts + maintenance
 *   Photos & Site Plans  → owner + maintenance
 *   Compliance           → owner only
 *   Reports & Technical  → owner only
 * Owner can override per-user via the portal-users admin.
 */

const CATEGORY_PERM = {
  contract:   "portal.docs.contracts.read",
  proposal:   "portal.docs.proposals.read",
  manual:     "portal.docs.manuals.read",
  warranty:   "portal.docs.warranties.read",
  account:    "portal.docs.account.read",
  notice:     "portal.docs.notices.read",
  site_plan:  "portal.docs.photos.read",
  compliance: "portal.docs.compliance.read",
  report:     "portal.docs.reports.read",
};

const CATEGORIES = [
  { key: "contract",  label: "Contracts & SLAs",           icon: FileSignature, accent: "#1FB6C1" },
  { key: "proposal",  label: "Proposals & Designs",        icon: FileCheck2,    accent: "#A78BFA" },
  { key: "manual",    label: "Manuals & Datasheets",       icon: BookOpen,      accent: "#F59E0B" },
  { key: "warranty",  label: "Warranties",                 icon: ShieldCheck,   accent: "#10B981" },
  { key: "account",   label: "Account Docs",               icon: Landmark,      accent: "#06B6D4" },
  { key: "notice",    label: "Notices & Bulletins",        icon: Megaphone,     accent: "#EAB308" },
  { key: "site_plan", label: "Photos & Site Plans",        icon: Map,           accent: "#F97316" },
  { key: "compliance",label: "Compliance (SHEQ / Insurance / Permits)", icon: FileArchive, accent: "#6366F1" },
  { key: "report",    label: "Reports & Technical",        icon: FileText,      accent: "#EF4444" },
];

export default function CustomerProfileDocumentsBlock({
  customerId,
  apiBase,           // e.g. `/customers/${customerId}` or `/portal`
  canEdit = true,    // staff -> true; client portal -> false by default
  canDelete = false, // admins/managers only
  initialOpen = true,
  hideHeader = false, // when wrapped in a FoldBlock, hide our inner header
}) {
  const base = apiBase || `/customers/${customerId}`;
  const isPortal = base === "/portal" || base.startsWith("/portal");
  // Iter 427.p40 — Perms context is always available inside PortalLayout;
  // outside the portal it returns `{ perms: null, loading: true }` and
  // we fall back to showing every folder (staff sees all).
  const { can, loading: permsLoading } = usePortalPerms();
  const visibleCategories = useMemo(() => {
    if (!isPortal) return CATEGORIES;   // staff always sees all folders
    if (permsLoading) return [];         // fail-closed while loading
    return CATEGORIES.filter((c) => can(CATEGORY_PERM[c.key]));
  }, [isPortal, permsLoading, can]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [openSections, setOpenSections] = useState(() => {
    // Default: only first 3 sections expanded — keeps the page short.
    const s = {};
    CATEGORIES.forEach((c, i) => { s[c.key] = initialOpen ? i < 3 : false; });
    return s;
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${base}/profile-documents`);
      setDocs(Array.isArray(data) ? data : []);
    } catch {
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const list = Array.isArray(docs) ? docs : [];
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter(
      (d) =>
        (d.filename || "").toLowerCase().includes(s) ||
        (d.description || "").toLowerCase().includes(s) ||
        (d.tags || []).some((t) => (t || "").toLowerCase().includes(s)),
    );
  }, [docs, q]);

  const byCategory = useMemo(() => {
    const map = {};
    CATEGORIES.forEach((c) => { map[c.key] = []; });
    (Array.isArray(filtered) ? filtered : []).forEach((d) => {
      const k = map[d.category] ? d.category : "compliance";
      map[k].push(d);
    });
    return map;
  }, [filtered]);

  const toggleSection = (k) => setOpenSections((s) => ({ ...s, [k]: !s[k] }));
  const openAll = () => setOpenSections(() => {
    const s = {};
    visibleCategories.forEach((c) => { s[c.key] = true; });
    return s;
  });
  const closeAll = () => setOpenSections(() => {
    const s = {};
    visibleCategories.forEach((c) => { s[c.key] = false; });
    return s;
  });

  const uploadTo = async (categoryKey, files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (list.length === 0) return;
    const toastId = toast.loading(`Uploading ${list.length} file${list.length === 1 ? "" : "s"}…`);
    let ok = 0;
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`${base}/profile-documents?category=${encodeURIComponent(categoryKey)}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok += 1;
      } catch (e) {
        toast.error(`"${f.name}" failed: ${e?.response?.data?.detail || "upload error"}`);
      }
    }
    toast.dismiss(toastId);
    if (ok > 0) toast.success(`${ok} file${ok === 1 ? "" : "s"} uploaded.`);
    // 2026-02-18 Round D2 — also queue an OCR job for each image / PDF
    // so the doc shows up in /ocr/invoices for review. Fire-and-forget,
    // never blocks. Customer id only — site/project unknown here.
    const ocrCount = await kickOffAutoOcrBatch(list, {
      customer_id: customerId || null,
      source: `profile_doc_${categoryKey}`,
    });
    if (ocrCount > 0) {
      toast.success(`${ocrCount} doc${ocrCount === 1 ? "" : "s"} queued for OCR review.`);
    }
    load();
  };

  const remove = async (docId) => {
    if (!confirm("Delete this document?")) return;
    try {
      await api.delete(`${base}/profile-documents/${docId}`);
      toast.success("Document deleted.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  const patch = async (docId, body) => {
    try {
      await api.patch(`${base}/profile-documents/${docId}`, body);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed.");
    }
  };

  // [Iter 420] Project link/unlink from the Customer documents view.
  // Uses the same POST/DELETE /projects/{pid}/documents/{doc_id}/link
  // endpoints as the Project's Documents tab, so a doc linked here
  // shows up immediately when the project opens.
  const linkProject = async (docId, projectId) => {
    try {
      await api.post(`/projects/${projectId}/documents/${docId}/link`);
      toast.success("Linked to project.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link failed.");
    }
  };

  const unlinkProject = async (docId, projectId) => {
    try {
      await api.delete(`/projects/${projectId}/documents/${docId}/link`);
      toast.success("Unlinked.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlink failed.");
    }
  };

  const totalCount = docs.length;
  const matchCount = filtered.length;

  return (
    <div className="hv-card p-4 md:p-5" data-testid="profile-documents-block">
      {/* Header + search */}
      {!hideHeader && (
        <div className="flex items-start md:items-center gap-3 flex-wrap mb-3">
          <div className="w-9 h-9 rounded-md bg-[#1FB6C1]/15 border border-[#1FB6C1]/40 flex items-center justify-center shrink-0">
            <FolderOpen className="w-4 h-4 text-[#1FB6C1]" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-xl md:text-2xl font-bold">Documents</h2>
            <div className="text-xs text-[#a0aabf]">
              {loading
                ? "Loading…"
                : (q
                    ? `${matchCount} of ${totalCount} match "${q}"`
                    : `${totalCount} document${totalCount === 1 ? "" : "s"} across ${visibleCategories.length} folders`)}
            </div>
          </div>
        </div>
      )}

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#647087]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search across every folder — filename, description, or tag…"
          className="hv-input hv-input-search w-full"
          data-testid="pd-search"
        />
      </div>

      {/* Folders */}
      <div className="space-y-2">
        {visibleCategories.map((cat) => (
          <SectionRow
            key={cat.key}
            cat={cat}
            docs={byCategory[cat.key]}
            open={!!openSections[cat.key]}
            onToggle={() => toggleSection(cat.key)}
            onUpload={canEdit ? (fs) => uploadTo(cat.key, fs) : null}
            onRemove={canDelete ? remove : null}
            onPatch={canEdit ? patch : null}
            canEdit={canEdit}
            customerId={customerId}
            onLinkProject={canEdit ? linkProject : null}
            onUnlinkProject={canEdit ? unlinkProject : null}
          />
        ))}
        {/* Iter 428.p6 — Reports & Technical. Sits alongside the
            manually-uploaded folders (Contracts / Proposals / Manuals
            etc.) so generated engineering reports land in the same
            Documents tab the user already reaches for, instead of a
            separate panel on the side. */}
        {!isPortal && (
          <ReportsTechnicalSection customerId={customerId} />
        )}
      </div>
    </div>
  );
}

function SectionRow({ cat, docs, open, onToggle, onUpload, onRemove, onPatch, canEdit, customerId, onLinkProject, onUnlinkProject }) {
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (onUpload && e.dataTransfer?.files?.length) onUpload(e.dataTransfer.files);
  };

  const Icon = cat.icon;
  return (
    <div
      className={`border border-white/10 rounded-md ${dragOver ? "bg-[#1fb6c1]/5 border-[#1fb6c1]/50" : ""}`}
      onDragOver={(e) => { if (onUpload) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      data-testid={`pd-section-${cat.key}`}
    >
      <div className="flex items-center gap-2 p-3">
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center gap-2 min-w-0 flex-1 text-left hover:opacity-80"
          data-testid={`pd-section-${cat.key}-toggle`}
        >
          {open ? <ChevronDown className="w-4 h-4 text-[#647087] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#647087] shrink-0" />}
          <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0" style={{ background: `${cat.accent}22`, border: `1px solid ${cat.accent}55` }}>
            <Icon className="w-3.5 h-3.5" style={{ color: cat.accent }} />
          </div>
          <div className="font-medium text-sm truncate">{cat.label}</div>
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border" style={{ borderColor: `${cat.accent}40`, color: cat.accent }}>
            {docs.length}
          </span>
        </button>
        {onUpload && (
          <>
            <input
              ref={fileRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => { onUpload(e.target.files); if (fileRef.current) fileRef.current.value = ""; }}
              data-testid={`pd-section-${cat.key}-input`}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="hv-btn hv-btn-ghost text-xs shrink-0"
              data-testid={`pd-section-${cat.key}-upload`}
            >
              <Upload className="w-3 h-3" /> Upload
            </button>
          </>
        )}
      </div>

      {open && (
        <div className="border-t border-white/10 p-3">
          {docs.length === 0 ? (
            <div className="text-xs text-[#647087] italic py-4 text-center">
              {onUpload ? "Drag files here, or click Upload above." : "No documents."}
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {docs.map((d) => (
                <DocRow
                  key={d.id}
                  d={d}
                  onRemove={onRemove}
                  onPatch={onPatch}
                  canEdit={canEdit}
                  customerId={customerId}
                  onLinkProject={onLinkProject}
                  onUnlinkProject={onUnlinkProject}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DocRow({ d, onRemove, onPatch, canEdit, customerId, onLinkProject, onUnlinkProject }) {
  const [editing, setEditing] = useState(false);
  const [desc, setDesc] = useState(d.description || "");
  const [tags, setTags] = useState((d.tags || []).join(", "));
  const [busy, setBusy] = useState(false);
  // [Iter 420] Project picker for link-to-project.
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);

  const openProjectPicker = async () => {
    setProjectPickerOpen(true);
    if (projects.length > 0) return;
    setProjectsLoading(true);
    try {
      const { data } = await api.get("/projects", {
        params: { customer_id: customerId, limit: 100 },
      });
      const items = Array.isArray(data) ? data : (data?.items || []);
      setProjects(items);
    } catch {
      /* noop */
    } finally {
      setProjectsLoading(false);
    }
  };

  const linkedSet = new Set(d.project_ids || []);
  const availableProjects = projects.filter((p) => !linkedSet.has(p.id));

  const save = async () => {
    setBusy(true);
    await onPatch(d.id, {
      description: desc,
      tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
    });
    setBusy(false);
    setEditing(false);
  };

  return (
    <div className="flex items-start gap-3 py-2.5" data-testid={`pd-doc-${d.id}`}>
      <div className="w-8 h-8 rounded-md bg-white/[0.03] border border-white/10 flex items-center justify-center shrink-0">
        <FileText className="w-3.5 h-3.5 text-[#a0aabf]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={d.url}
            target="_blank"
            rel="noreferrer"
            className="text-sm font-medium hover:text-[#1fb6c1] truncate"
            data-testid={`pd-doc-${d.id}-open`}
          >
            {d.filename}
          </a>
          <span className="text-[10px] text-[#647087] shrink-0">{humanKB(d.size)}</span>
          {(d.tags || []).map((t) => (
            <span key={t} className="hv-chip text-[9px]"><Tag className="w-2.5 h-2.5" /> {t}</span>
          ))}
        </div>
        {editing ? (
          <div className="mt-2 space-y-2">
            <input
              className="hv-input text-xs"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="Description"
              data-testid={`pd-doc-${d.id}-desc`}
            />
            <input
              className="hv-input text-xs"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="Tags (comma-separated)"
              data-testid={`pd-doc-${d.id}-tags`}
            />
            <div className="flex gap-2">
              <button type="button" onClick={save} disabled={busy} className="hv-btn hv-btn-primary text-xs">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save
              </button>
              <button type="button" onClick={() => setEditing(false)} className="hv-btn hv-btn-ghost text-xs">
                <X className="w-3 h-3" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="text-xs text-[#a0aabf] mt-0.5 truncate">
              {d.description ? d.description : <span className="text-[#647087] italic">No description</span>}
              {d.uploaded_by_name && <span className="text-[#647087]"> · by {d.uploaded_by_name}</span>}
              {d.uploaded_at && <span className="text-[#647087]"> · {new Date(d.uploaded_at).toLocaleDateString("en-GB")}</span>}
            </div>
            {Array.isArray(d.project_ids) && d.project_ids.length > 0 && (
              <div
                className="flex flex-wrap items-center gap-1 mt-1"
                data-testid={`pd-doc-${d.id}-project-chips`}
              >
                <span className="text-[10px] uppercase tracking-wider text-[#647087]">Linked to:</span>
                {d.project_ids.map((pid) => {
                  const proj = projects.find((p) => p.id === pid);
                  const label = proj?.short_id || proj?.title || (proj?.name) || "Project";
                  return (
                    <span
                      key={pid}
                      className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-[#A78BFA]/40 bg-[#A78BFA]/10 text-[#A78BFA]"
                      data-testid={`pd-doc-${d.id}-project-chip-${pid}`}
                    >
                      <a href={`/projects/${pid}`} className="inline-flex items-center gap-1 hover:underline">
                        <FolderOpen className="w-2.5 h-2.5" /> {label}
                      </a>
                      {onUnlinkProject && (
                        <button
                          type="button"
                          onClick={() => onUnlinkProject(d.id, pid)}
                          className="hover:text-rose-300 ml-1"
                          title="Unlink from project"
                          data-testid={`pd-doc-${d.id}-project-unlink-${pid}`}
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </span>
                  );
                })}
              </div>
            )}
            {/* [Iter 420] Link-to-project picker — always visible when
                canEdit so users can link an existing customer doc to
                any of that customer's projects in one click. */}
            {canEdit && onLinkProject && customerId && (
              <div className="mt-1 relative" data-testid={`pd-doc-${d.id}-project-linker`}>
                <button
                  type="button"
                  onClick={openProjectPicker}
                  className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-white/10 text-[#a0aabf] hover:text-white hover:border-[#A78BFA]/50"
                  data-testid={`pd-doc-${d.id}-project-link-open`}
                >
                  <Link2 className="w-2.5 h-2.5" /> Link to project
                </button>
                {projectPickerOpen && (
                  <div
                    className="absolute z-30 mt-1 left-0 w-64 max-h-64 overflow-y-auto rounded-md border border-white/10 bg-[#0a0b0e] shadow-xl p-2 space-y-1"
                    data-testid={`pd-doc-${d.id}-project-list`}
                  >
                    <div className="flex items-center justify-between text-[10px] text-[#647087] uppercase tracking-wider px-1 pb-1 border-b border-white/5">
                      <span>Select project</span>
                      <button
                        type="button"
                        onClick={() => setProjectPickerOpen(false)}
                        className="text-[#647087] hover:text-white"
                        data-testid={`pd-doc-${d.id}-project-picker-close`}
                      >
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </div>
                    {projectsLoading ? (
                      <div className="text-[11px] text-[#647087] flex items-center gap-1 px-1 py-1">
                        <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                      </div>
                    ) : availableProjects.length === 0 ? (
                      <div className="text-[11px] text-[#647087] italic px-1 py-1">
                        No other projects for this customer.
                      </div>
                    ) : (
                      availableProjects.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={async () => {
                            await onLinkProject(d.id, p.id);
                            setProjectPickerOpen(false);
                          }}
                          className="w-full text-left text-[11px] px-2 py-1 rounded hover:bg-white/[0.06] text-white"
                          data-testid={`pd-doc-${d.id}-project-pick-${p.id}`}
                        >
                          <span className="font-mono text-[#A78BFA]">{p.short_id || p.id.slice(0, 8)}</span>
                          {" "}
                          {p.title || p.name || ""}
                          {p.status && <span className="text-[9px] text-[#647087] ml-1">· {p.status}</span>}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <a
          href={d.url}
          download={d.filename}
          className="hv-btn hv-btn-ghost px-2 py-1 text-xs"
          title="Download"
          data-testid={`pd-doc-${d.id}-download`}
        >
          <Download className="w-3 h-3" />
        </a>
        {canEdit && !editing && (
          <button
            type="button"
            onClick={() => { setDesc(d.description || ""); setTags((d.tags || []).join(", ")); setEditing(true); }}
            className="hv-btn hv-btn-ghost px-2 py-1 text-xs"
            title="Edit"
            data-testid={`pd-doc-${d.id}-edit`}
          >
            <Pencil className="w-3 h-3" />
          </button>
        )}
        {onRemove && (
          <button
            type="button"
            onClick={() => onRemove(d.id)}
            className="hv-btn hv-btn-ghost px-2 py-1 text-xs text-rose-300 hover:text-rose-200"
            title="Delete"
            data-testid={`pd-doc-${d.id}-delete`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}

function humanKB(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
